import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react"
import {
  PendingCvUpload,
  CvUploadStatus,
} from "@/modules/recruitment/candidates/dtos/cv-upload"

interface CvUploadContextValue {
  uploads: PendingCvUpload[]
  /** Count of uploads that are still Pending or Processing */
  activeCount: number
  addUpload: (upload: PendingCvUpload) => void
  updateUpload: (
    correlationId: string,
    patch: Partial<Omit<PendingCvUpload, "correlationId" | "fileName">>,
  ) => void
  /** Swap a temporary client-side ID for the real server correlationId */
  updateUploadId: (oldId: string, newId: string) => void
  clearCompleted: () => void
}

const CvUploadContext = createContext<CvUploadContextValue | null>(null)

export function CvUploadProvider({ children }: { children: ReactNode }) {
  const [uploads, setUploads] = useState<PendingCvUpload[]>([])

  const addUpload = useCallback((upload: PendingCvUpload) => {
    setUploads((prev) => [...prev, upload])
  }, [])

  const updateUpload = useCallback(
    (
      correlationId: string,
      patch: Partial<Omit<PendingCvUpload, "correlationId" | "fileName">>,
    ) => {
      setUploads((prev) =>
        prev.map((u) =>
          u.correlationId === correlationId ? { ...u, ...patch } : u,
        ),
      )
    },
    [],
  )

  const updateUploadId = useCallback((oldId: string, newId: string) => {
    setUploads((prev) =>
      prev.map((u) =>
        u.correlationId === oldId ? { ...u, correlationId: newId } : u,
      ),
    )
  }, [])

  const clearCompleted = useCallback(() => {
    setUploads((prev) =>
      prev.filter((u) => u.status !== "Completed" && u.status !== "Failed"),
    )
  }, [])

  const activeCount = uploads.filter(
    (u): boolean =>
      (u.status as CvUploadStatus) === "Pending" ||
      (u.status as CvUploadStatus) === "Processing",
  ).length

  return (
    <CvUploadContext.Provider
      value={{
        uploads,
        activeCount,
        addUpload,
        updateUpload,
        updateUploadId,
        clearCompleted,
      }}
    >
      {children}
    </CvUploadContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCvUploadContext(): CvUploadContextValue {
  const ctx = useContext(CvUploadContext)
  if (!ctx)
    throw new Error("useCvUploadContext must be used inside <CvUploadProvider>")
  return ctx
}
